<template>
    <div class="mt-6 mb-2">
        <div class="flex sm:justify-between bg-minplan-beige text-minplan-blue">
            <div class="flex min-w-0 w-full md:w-5/8 xl:w-2/4">
                <p class="hidden md:flex w-1/4">
                    <img
                        class="w-80 h-auto object-cover"
                        :src="
                            project.image
                                ? route('media.restore', { filename: project.image })
                                : '/images/noimg.png'
                        "
                        alt=""
                    />
                </p>
                <div class="flex min-w-0 p-4 w-full md:w-3/4">
                    <div>
                        <LocationMarkerIcon
                            class="mr-2 h-5 w-5 text-minplan-darkgreybeige"
                        />
                        <button
                            type="button"
                            v-if="editBaseLink"
                            @click="editBase"
                            class="block"
                        >
                            <PencilIcon
                                class="mr-2 mt-3 h-5 w-5 text-minplan-darkbeige hover:text-minplan-darkgreybeige"
                            />
                        </button>
                    </div>
                    <div>
                        <!--
                          NUMMER STATT `name` (Auftrag Luca, 24.09.2026).
                          `apartment.name` ist `getDisplayName()` — Nummer PLUS
                          Kaeufer-Nachnamen. Die Namen stehen in der Spalte
                          DANEBEN; im Titel waren sie eine Dopplung und kosteten
                          genau die Breite, an der der Kopf ueberlief.
                          `ApartmentResource` liefert beide Felder, hier reicht
                          die Nummer.
                          NUR DIE DETAILSEITE: Wohnungsliste, Exporte,
                          Dokumentverteilung und die SORTIERUNG
                          (`ApartmentOrdering`, `BuildingStructure`) haengen
                          weiterhin an `getDisplayName()` und bleiben unberuehrt.
                      -->
                        <span class="break-words min-w-0 inline-block w-full font-bold"
                            >{{ $t("apartments.apartment") }} {{ apartment.number }}</span
                        ><br />
                        <span
                            >{{ project.street }}, {{ project.zip }}
                            {{ project.city }}</span
                        ><br />
                        <span class="mt-2 inline-block text-minplan-darkbeige"
                            >{{ apartment.rooms }} {{ $t("apartments.rooms") }}
                            {{ apartment.floor }} | {{ apartment.area }}&#13217;
                            {{ $t("apartments.area") }}</span
                        >
                        <!-- Entscheid Peter, 22.09.2026: der Parkplatz auf eigener
                             Zeile, damit die Zeile darüber nicht länger wird. -->
                        <span
                            v-if="apartment.parking"
                            data-parking
                            class="block text-minplan-darkbeige"
                            >{{ $t("apartments.parking") }} {{ apartment.parking }}</span
                        >
                    </div>
                </div>
            </div>
            <div class="hidden min-w-0 p-4 md:flex md:w-3/8 xl:w-1/4">
                <div>
                    <KeyIcon class="mr-2 h-5 w-5 text-minplan-darkgreybeige" />
                    <button
                        type="button"
                        @click="createBuyer"
                        v-if="createBuyerLink && buyers.length < 2"
                    >
                        <PlusSmIcon
                            class="mr-2 mt-3 h-5 w-5 text-minplan-darkbeige hover:text-minplan-darkgreybeige"
                        />
                    </button>
                </div>
                <div>
                    <span class="truncate inline-block w-full font-bold">
                        {{ $t("apartments.buyers") }}
                    </span>

                    <!-- Ein Käufer ist ein Block mit ZWEI festen Zeilen
                         (Entscheid Peter, 31.08.2026): oben der Name mit dem
                         Zustand, unten der Hinweis und die Aktionen. Die Spalte
                         ist schmal; ohne feste Aufteilung rutschte alles über
                         drei Zeilen und die Zustandsmarke brach mitten im Wort
                         um. Der Einladen-Knopf steht zuerst, weil er die
                         eigentliche Handlung ist. -->
                    <div v-for="buyer in buyers" :key="buyer.id" class="mt-2 first:mt-1">
                        <div class="flex items-center gap-2 min-w-0">
                            <span class="break-words min-w-0"
                                >{{ buyer.firstname }} {{ buyer.lastname }}</span
                            >

                            <span
                                class="flex-shrink-0"
                                v-if="buyer.accepted_invite && resendBuyerInviteLink"
                                :title="$t('apartments.inviteAccepted')"
                            >
                                <CheckIcon
                                    class="h-4 w-4 inline rounded bg-lime-100 text-lime-700"
                                />
                            </span>

                            <span
                                class="flex-shrink-0"
                                v-if="
                                    buyer.invited &&
                                    !buyer.accepted_invite &&
                                    resendBuyerInviteLink
                                "
                                :title="$t('apartments.inviteNotAccepted')"
                            >
                                <ClockIcon
                                    class="h-4 w-4 inline rounded bg-amber-100 text-amber-700"
                                />
                            </span>
                        </div>

                        <!-- Kein Text «nicht eingeladen» (Entscheid Peter,
                             31.08.2026): Die Beschriftung des Knopfes sagt es
                             bereits. «Einladen» heisst noch nie eingeladen,
                             «Erneut einladen» heisst eingeladen und noch nicht
                             angenommen, kein Knopf heisst angenommen. -->
                        <div class="flex items-center gap-2 mt-0.5">
                            <span class="ml-auto flex items-center gap-1.5 flex-shrink-0">
                                <button
                                    type="button"
                                    v-if="
                                        !buyer.accepted_invite &&
                                        resendBuyerInviteLink &&
                                        buyer.email
                                    "
                                    class="whitespace-nowrap text-xs text-minplan-darkbeige rounded border border-minplan-darkbeige hover:text-minplan-blue px-2 py-0.5 cursor-pointer"
                                    @click="resendInvite(buyer.id)"
                                >
                                    {{
                                        buyer.invited
                                            ? $t("apartments.resend")
                                            : $t("apartments.invite")
                                    }}
                                </button>

                                <!-- Entscheid Peter, 22.09.2026: die E-Mail ist freiwillig, bis
                                     eingeladen wird. Fehlt sie, führt der Knopf in die Maske,
                                     wo sie sich ergänzen lässt, statt eine Einladung zu
                                     versprechen, die nicht hinausgehen kann. -->
                                <button
                                    type="button"
                                    v-if="
                                        !buyer.accepted_invite &&
                                        resendBuyerInviteLink &&
                                        !buyer.email &&
                                        editBuyerRoute
                                    "
                                    class="whitespace-nowrap text-xs text-minplan-darkbeige rounded border border-minplan-darkbeige hover:text-minplan-blue px-2 py-0.5 cursor-pointer"
                                    @click="
                                        $inertia.visitInModal(
                                            route(editBuyerRoute, {
                                                apartment: apartment.uuid,
                                                buyer: buyer.id,
                                            })
                                        )
                                    "
                                >
                                    {{ $t("apartments.addEmail") }}
                                </button>

                                <button
                                    type="button"
                                    v-if="editBuyerRoute"
                                    :title="$t('actions.edit')"
                                    @click="
                                        $inertia.visitInModal(
                                            route(editBuyerRoute, {
                                                apartment: apartment.uuid,
                                                buyer: buyer.id,
                                            })
                                        )
                                    "
                                >
                                    <PencilIcon
                                        class="h-4 w-4 text-minplan-darkbeige hover:text-minplan-darkgreybeige"
                                    />
                                </button>

                                <button
                                    type="button"
                                    v-if="deleteBuyerRoute"
                                    :title="$t('actions.delete')"
                                    @click="
                                        $inertia.visitInModal(
                                            route(deleteBuyerRoute, {
                                                apartment: apartment.uuid,
                                                buyer: buyer.id,
                                            })
                                        )
                                    "
                                >
                                    <TrashIcon
                                        class="h-4 w-4 text-minplan-darkbeige hover:text-minplan-darkgreybeige"
                                    />
                                </button>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="hidden min-w-0 xl:flex flex-col p-4 w-1/4 space-y-3">
                <div class="flex items-start">
                    <div class="flex flex-col items-center mr-2">
                        <UserIcon class="h-5 w-5 text-minplan-blue flex-shrink-0" />
                        <button
                            type="button"
                            v-if="editAdvisorLink"
                            @click="
                                $inertia.visitInModal(
                                    route('advisors.apartments.edit.advisor', {
                                        apartment: apartment.uuid,
                                    })
                                )
                            "
                            class="mt-2 text-minplan-darkbeige hover:text-minplan-darkgreybeige"
                        >
                            <PencilIcon
                                class="mt-3 h-5 w-5 text-minplan-darkbeige hover:text-minplan-darkgreybeige"
                            />
                        </button>
                    </div>
                    <div class="min-w-0 flex-1">
                        <span class="truncate inline-block font-bold w-full">
                            {{ $t("apartments.buyerAssistance") }}
                        </span>
                        <span class="block truncate mt-1" :title="apartment.advisor">{{
                            apartment.advisor
                        }}</span>
                        <span
                            class="block truncate text-minplan-darkbeige"
                            :title="project.agency"
                            >{{ project.agency }}</span
                        >
                    </div>
                </div>
                <div v-if="apartment.advisor_email" class="flex items-center min-w-0">
                    <a
                        :href="
                            'mailto:' +
                            apartment.advisor_email +
                            '?subject=' +
                            $t('projects.project') +
                            ' ' +
                            project.name +
                            ' ' +
                            $t('apartments.apartment') +
                            ' ' +
                            apartment.name
                        "
                        class="flex items-center min-w-0 hover:text-minplan-blue text-minplan-darkbeige text-sm w-full"
                        :title="apartment.advisor_email"
                    >
                        <MailIcon class="mr-2 h-5 w-5 flex-shrink-0" />
                        <span class="truncate">{{ apartment.advisor_email }}</span>
                    </a>
                </div>
                <div v-if="apartment.advisor_mobile" class="flex items-center min-w-0">
                    <a
                        :href="
                            'tel:' +
                            (apartment.advisor_mobile
                                ? apartment.advisor_mobile.replace(/[^0-9]/g, '')
                                : '')
                        "
                        class="flex items-center min-w-0 hover:text-minplan-blue text-minplan-darkbeige text-sm w-full"
                        :title="apartment.advisor_mobile"
                    >
                        <DeviceMobileIcon class="mr-2 h-5 w-5 flex-shrink-0" />
                        <span class="truncate">{{ apartment.advisor_mobile }}</span>
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {
    KeyIcon,
    LocationMarkerIcon,
    MailIcon,
    DeviceMobileIcon,
    PencilIcon,
    PlusSmIcon,
    TrashIcon,
    TruckIcon,
    UserIcon,
    CheckIcon,
    ClockIcon,
} from "@heroicons/vue/outline";
import axios from "axios";

export default {
    props: [
        "apartment",
        "project",
        "buyers",
        "editBaseLink",
        "createBuyerLink",
        "editBuyerRoute",
        "deleteBuyerRoute",
        "editAdvisorLink",
        "resendBuyerInviteLink",
    ],
    components: {
        PencilIcon,
        TruckIcon,
        LocationMarkerIcon,
        MailIcon,
        DeviceMobileIcon,
        PlusSmIcon,
        KeyIcon,
        TrashIcon,
        UserIcon,
        CheckIcon,
        ClockIcon,
    },
    methods: {
        editBase() {
            this.$inertia.visitInModal(this.editBaseLink);
        },
        createBuyer() {
            this.$inertia.visitInModal(this.createBuyerLink);
        },
        editAdvisor() {
            this.$inertia.visitInModal(this.editAdvisorLink);
        },
        resendInvite(buyerUuid) {
            axios
                .put(
                    this.route(this.resendBuyerInviteLink, {
                        apartment: this.apartment.uuid,
                        buyer: buyerUuid,
                    }),
                    {}
                )
                .then((_) => {
                    this.$notify({
                        group: "success",
                        title: "actions.success",
                        text: "apartments.inviteSent",
                    });
                });
        },
    },
};
</script>

<style scoped></style>
